import React, { useEffect, useState } from "react";
import { BellRing, BellOff, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const b64ToU8 = (v) => {
  const pad = "=".repeat((4 - (v.length % 4)) % 4);
  const b = (v + pad).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(b), (c) => c.charCodeAt(0));
};
const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIosBrowser = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.navigator.standalone;

export function PushToggle() {
  const [sub, setSub] = useState(null);
  const [devices, setDevices] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported()) return;
    navigator.serviceWorker.ready.then((reg) => reg.pushManager.getSubscription()).then(setSub).catch(console.error);
    api.get("/push/status").then((r) => setDevices(r.data.devices || 0)).catch(console.error);
  }, []);

  const enable = async () => {
    if (!supported()) { toast.error("Ton navigateur ne supporte pas les notifications push"); return; }
    if (isIosBrowser()) { toast.error("Sur iPhone : ajoute d'abord le site à l'écran d'accueil (Partager → Sur l'écran d'accueil), puis active depuis l'appli.", { duration: 8000 }); return; }
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Autorisation refusée — active les notifications dans les réglages du navigateur"); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public-key");
      const s = (await reg.pushManager.getSubscription()) || await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToU8(data.key) });
      const r = await api.post("/push/subscribe", s.toJSON());
      setSub(s); setDevices(r.data.devices || 1);
      toast.success("Notifications push activées sur cet appareil 🔔", { style: { background: "#121212", color: "#9dff00", border: "1px solid #9dff00" } });
    } catch (e) { toast.error(e?.response?.data?.detail || `Impossible d'activer les notifications${e?.message ? " : " + e.message.slice(0, 90) : ""}`); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    if (!sub) return;
    setBusy(true);
    try {
      await api.delete("/push/subscribe", { data: sub.toJSON() });
      await sub.unsubscribe();
      setSub(null); setDevices((d) => Math.max(0, d - 1));
      toast("Notifications push désactivées sur cet appareil");
    } catch (e) { toast.error("Erreur"); }
    finally { setBusy(false); }
  };

  const test = async () => {
    try { const { data } = await api.post("/push/test"); toast.success(data.sent > 0 ? `Notification test envoyée (${data.sent} appareil${data.sent > 1 ? "s" : ""})` : "Aucun appareil abonné"); }
    catch { toast.error("Erreur d'envoi"); }
  };

  return (
    <div data-testid="push-toggle" className={`flex flex-wrap items-center gap-2 border px-3 py-2 text-xs ${sub ? "border-neon-green/60 bg-neon-green/5" : "border-zinc-800 bg-ink-surface"}`}>
      {sub ? <BellRing className="h-4 w-4 text-neon-green shrink-0" /> : <BellOff className="h-4 w-4 text-zinc-500 shrink-0" />}
      <span className="text-zinc-300">
        {sub ? <><span className="text-neon-green font-bold">Push activé</span> — les commandes sonnent même écran verrouillé</> : <><span className="text-white font-bold">Push mobile</span> — reçois les commandes même appli fermée</>}
        {devices > 0 && <span className="text-zinc-500 ml-1.5 inline-flex items-center gap-1"><Smartphone className="h-3 w-3" /> {devices} appareil{devices > 1 ? "s" : ""}</span>}
      </span>
      <span className="ml-auto flex items-center gap-1.5">
        {sub && <button data-testid="push-test-btn" onClick={test} className="uppercase font-bold border border-zinc-700 text-zinc-300 px-2.5 py-1.5 hover:border-neon-green hover:text-neon-green transition-colors">Tester</button>}
        {sub ? (
          <button data-testid="push-disable-btn" disabled={busy} onClick={disable} className="uppercase font-bold border border-zinc-700 text-zinc-400 px-2.5 py-1.5 hover:text-red-400 hover:border-red-400 transition-colors disabled:opacity-40">Désactiver</button>
        ) : (
          <button data-testid="push-enable-btn" disabled={busy} onClick={enable} className="uppercase font-bold bg-neon-green text-black px-3 py-1.5 hover:bg-white transition-colors disabled:opacity-40">Activer les notifications</button>
        )}
      </span>
    </div>
  );
}
